/**
 * Client-Side Encryption Helpers
 * Provides encryption utilities for sensitive data before transmission
 * Uses Web Crypto API for secure cryptographic operations
 */

export interface EncryptionConfig {
  algorithm: string;
  keyLength: number;
  iterations: number;
  enabled: boolean;
}

export class EncryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EncryptionError';
  }
}

class EncryptionService {
  private config: EncryptionConfig;
  private encoder = new TextEncoder();
  private decoder = new TextDecoder();

  constructor(config?: Partial<EncryptionConfig>) {
    this.config = {
      algorithm: 'AES-GCM',
      keyLength: 256,
      iterations: 100000,
      enabled: true,
      ...config,
    };

    // Check for Web Crypto API support
    if (this.config.enabled && !window.crypto?.subtle) {
      console.warn('[ENCRYPTION] Web Crypto API not available. Encryption disabled.');
      this.config.enabled = false;
    }
  }

  /**
   * Encrypt a string value
   * @param plaintext - The string to encrypt
   * @param password - Optional password for encryption (uses session key if not provided)
   * @returns Base64-encoded encrypted string with IV prepended
   */
  async encrypt(plaintext: string, password?: string): Promise<string> {
    if (!this.config.enabled) {
      return plaintext;
    }

    try {
      // Generate a random IV (Initialization Vector)
      const iv = window.crypto.getRandomValues(new Uint8Array(12));

      // Derive encryption key
      const key = await this.deriveKey(password || this.getSessionKey());

      // Encrypt the data
      const encrypted = await window.crypto.subtle.encrypt(
        {
          name: this.config.algorithm,
          iv,
        },
        key,
        this.encoder.encode(plaintext)
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Return as base64
      return this.arrayBufferToBase64(combined);
    } catch (error) {
      throw new EncryptionError(
        `Encryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Decrypt a string value
   * @param ciphertext - Base64-encoded encrypted string
   * @param password - Optional password for decryption (uses session key if not provided)
   * @returns Decrypted plaintext string
   */
  async decrypt(ciphertext: string, password?: string): Promise<string> {
    if (!this.config.enabled) {
      return ciphertext;
    }

    try {
      // Decode from base64
      const combined = this.base64ToArrayBuffer(ciphertext);

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      // Derive decryption key
      const key = await this.deriveKey(password || this.getSessionKey());

      // Decrypt the data
      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: this.config.algorithm,
          iv,
        },
        key,
        encrypted
      );

      return this.decoder.decode(decrypted);
    } catch (error) {
      throw new EncryptionError(
        `Decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Encrypt an object
   * @param obj - Object to encrypt
   * @param password - Optional password for encryption
   * @returns Base64-encoded encrypted string
   */
  async encryptObject<T extends Record<string, unknown>>(
    obj: T,
    password?: string
  ): Promise<string> {
    const json = JSON.stringify(obj);
    return await this.encrypt(json, password);
  }

  /**
   * Decrypt to an object
   * @param ciphertext - Base64-encoded encrypted string
   * @param password - Optional password for decryption
   * @returns Decrypted object
   */
  async decryptObject<T extends Record<string, unknown>>(
    ciphertext: string,
    password?: string
  ): Promise<T> {
    const json = await this.decrypt(ciphertext, password);
    return JSON.parse(json) as T;
  }

  /**
   * Hash a value using SHA-256
   * Useful for one-way hashing (passwords, etc.)
   */
  async hash(value: string): Promise<string> {
    if (!window.crypto?.subtle) {
      throw new EncryptionError('Web Crypto API not available');
    }

    const buffer = await window.crypto.subtle.digest(
      'SHA-256',
      this.encoder.encode(value)
    );

    return this.arrayBufferToHex(buffer);
  }

  /**
   * Generate a random encryption key
   * Returns a base64-encoded key
   */
  async generateKey(): Promise<string> {
    const key = window.crypto.getRandomValues(new Uint8Array(32)); // 256 bits
    return this.arrayBufferToBase64(key);
  }

  /**
   * Derive a cryptographic key from a password
   */
  private async deriveKey(password: string): Promise<CryptoKey> {
    // Import password as key material
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      this.encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive key using PBKDF2
    const salt = this.encoder.encode('withinehr-widget-salt'); // In production, use random salt per encryption

    return await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: this.config.iterations,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: this.config.algorithm, length: this.config.keyLength },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Get or create session encryption key
   */
  private getSessionKey(): string {
    let key = sessionStorage.getItem('ww_enc_key');

    if (!key) {
      // Generate new session key
      const keyArray = window.crypto.getRandomValues(new Uint8Array(32));
      key = this.arrayBufferToBase64(keyArray);
      sessionStorage.setItem('ww_enc_key', key);
    }

    return key;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64 string to Uint8Array
   */
  private base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Convert ArrayBuffer to hex string
   */
  private arrayBufferToHex(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    return Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Clear session encryption key
   */
  clearSessionKey(): void {
    sessionStorage.removeItem('ww_enc_key');
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<EncryptionConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Singleton instance
let encryptionServiceInstance: EncryptionService | null = null;

export function getEncryptionService(
  config?: Partial<EncryptionConfig>
): EncryptionService {
  if (!encryptionServiceInstance) {
    encryptionServiceInstance = new EncryptionService(config);
  }
  return encryptionServiceInstance;
}

export const encryptionService = getEncryptionService();

/**
 * Utility: Encrypt specific fields in an object
 * @param obj - Object containing data
 * @param fields - Array of field names to encrypt
 * @returns New object with specified fields encrypted
 */
export async function encryptFields<T extends Record<string, unknown>>(
  obj: T,
  fields: (keyof T)[]
): Promise<T> {
  const result = { ...obj };

  for (const field of fields) {
    if (field in result && typeof result[field] === 'string') {
      result[field] = (await encryptionService.encrypt(
        result[field] as string
      )) as T[keyof T];
    }
  }

  return result;
}

/**
 * Utility: Decrypt specific fields in an object
 * @param obj - Object containing encrypted data
 * @param fields - Array of field names to decrypt
 * @returns New object with specified fields decrypted
 */
export async function decryptFields<T extends Record<string, unknown>>(
  obj: T,
  fields: (keyof T)[]
): Promise<T> {
  const result = { ...obj };

  for (const field of fields) {
    if (field in result && typeof result[field] === 'string') {
      result[field] = (await encryptionService.decrypt(
        result[field] as string
      )) as T[keyof T];
    }
  }

  return result;
}
